import type { DAOCoinLimitOrderEntryResponse } from "deso-protocol";

import type {
  DesoTokenLimitOrder,
  DeSoTokenLimitOrderSubscriptionSubscription,
} from "@/graphql/codegen/graphql";
import type { BID_OR_ASK } from "@/hooks/use-token-orderbook";
import {
  ASK,
  BID,
  BID_OR_ASK_OP,
  stripCommas,
} from "@/hooks/use-token-orderbook";
import { nanosToInteger, tokenNanosToInteger } from "@/utils/currency";

export const zero = BigInt("0");
const ten = BigInt("10");
export const oneE9 = BigInt("1000000000");
export const oneE18 = BigInt("1000000000000000000");
export const oneE38 = BigInt("100000000000000000000000000000000000000");
export const oneE76 = oneE38 * oneE38;

export const DESO = "DESO";

export function operationTypeToBidOrAskString(
  operationType: number | null | undefined,
): BID_OR_ASK {
  return bidOrAskToString(bidOrAsk(operationType));
}

export function bidOrAskToString(bidOrAskVal: BID_OR_ASK_OP): BID_OR_ASK {
  switch (bidOrAskVal) {
    case BID_OR_ASK_OP.ASK_OP:
      return ASK;
    case BID_OR_ASK_OP.BID_OP:
      return BID;
    default:
      throw new Error(`Unknown bidOrAsk: ${bidOrAskVal}`);
  }
}

export function bidOrAsk(
  operationType: number | null | undefined,
): BID_OR_ASK_OP {
  switch (operationType) {
    case 1:
      return BID_OR_ASK_OP.ASK_OP;
    case 2:
      return BID_OR_ASK_OP.BID_OP;
    default:
      throw new Error(`Unknown operationType: ${operationType}`);
  }
}
export function invertPrice(val: bigint): bigint {
  // Ex: say val = 3 but is multiplied by a scaling factor of 100, so val = 300 as a bigint
  //
  // If we want to invert 3 to compute 1/3 but want to do it using the scaled bigints,
  // we can use the math (100 * 100 / 300) = 33, which scales back down to 0.33
  //
  // This function achieves the same thing but for numbers with a scaling factor of 1e38
  return (oneE38 * oneE38) / val;
}

export function mulPriceAndQuantity(price: bigint, quantity: bigint): bigint {
  return (price * quantity) / oneE38;
}

export function priceDecimalStringToBigInt(price: string): bigint {
  return convertDecimalStringToBigInt(price, oneE38);
}

export function priceBigIntToDecimalString(
  price: bigint,
  toFixedDecimalPlaces = -1,
): string {
  return convertBigIntToDecimalString(price, oneE38, toFixedDecimalPlaces);
}

export function quantityDecimalStringToBigInt(
  quantity: string,
  scalingFactor: bigint = oneE18,
): bigint {
  return convertDecimalStringToBigInt(quantity, scalingFactor);
}

export function quantityBigIntToDecmialString(
  quantity: bigint,
  toFixedDecimalPlaces = -1,
  scalingFactor: bigint = oneE18,
): string {
  return convertBigIntToDecimalString(
    quantity,
    scalingFactor,
    toFixedDecimalPlaces,
  );
}

function parseFloatWithCommas(f: string): number {
  return parseFloat(f.replace(/,/g, ""));
}

export function convertDecimalStringToBigInt(
  inputDecimalString: string,
  scalingFactor: bigint,
): bigint {
  let decimalString = stripCommas(inputDecimalString);
  if (inputDecimalString.indexOf("e") > -1) {
    decimalString = parseFloatWithCommas(inputDecimalString).toLocaleString(
      "en-US",
      {
        maximumFractionDigits: 38,
        useGrouping: false,
      },
    );
  }
  let vals = decimalString.split(".");
  if (vals.length === 0) {
    vals = ["0", "0"];
  }
  // In this case, we had a whole number like 123, with no decimal
  // so we add a "0" as the decimal.
  if (vals.length !== 2) {
    vals.push("0");
  }
  // This can happen if we have something like ".123"
  if (vals[0] === "") {
    vals[0] = "0";
  }
  // This can happen if we have something like "123."
  if (vals[1] === "") {
    vals[1] = "0";
  }

  const numDecimalPlacesInString = vals[1].length;

  const wholePart = BigInt(vals[0]) * scalingFactor;
  let decimalPart = BigInt(vals[1]) * scalingFactor;

  // Given an input string 1.123, this produces
  // decimalPart = 123 * scalingFactor * (10 ^ -numDecimalPlacesInString)
  for (let i = 0; i < numDecimalPlacesInString; ++i) {
    decimalPart = decimalPart / ten;
  }

  return wholePart + decimalPart;
}

export function convertBigIntToDecimalString(
  val: bigint,
  scalingFactor: bigint,
  toFixedDecimalPlaces = -1,
): string {
  const wholeNumber = val / scalingFactor;
  const decimalPart = val % scalingFactor;

  if (decimalPart === zero || toFixedDecimalPlaces === 0) {
    return wholeNumber.toString();
  }

  const scalingFactorDigits = getNumDigits(scalingFactor);
  let decimalPartAsString = decimalPart.toString();

  // Left pad the decimal part with zeros
  if (decimalPartAsString.length !== scalingFactorDigits) {
    const decimalLeadingZeros = "0".repeat(
      scalingFactorDigits - decimalPartAsString.length - 1,
    );
    decimalPartAsString = `${decimalLeadingZeros}${decimalPartAsString}`;
  }

  // Trim trailing zeros
  decimalPartAsString = decimalPartAsString.replace(/\.0+$/, "");

  // Pad with additional trailing zeros if needed
  if (toFixedDecimalPlaces > 0) {
    decimalPartAsString =
      decimalPartAsString + "0".repeat(toFixedDecimalPlaces);
    decimalPartAsString = decimalPartAsString.substring(
      0,
      toFixedDecimalPlaces,
    );
  }

  return `${wholeNumber}.${decimalPartAsString}`;
}

function getNumDigits(val: bigint): number {
  let numDigits = 0;
  while (val !== zero) {
    ++numDigits;
    val = val / ten;
  }
  return numDigits;
}

// This calculates price as a bigint that's scaled up by 1e38 and then converts it back to a human-readable decimal string.
//  The denominator for the output price is determined by the operation type:
// - If operation type = BID, then price is the number of selling coins per buying coin
// - If operation type = ASK, then price is the number of buying coins per selling coin
function scaledExchangeRateToPriceString(
  scaledExchangeRate: string,
  buyingPublicKey: string,
  sellingPublicKey: string,
  operationType: "BID" | "ASK",
): string {
  let price = BigInt(scaledExchangeRate);
  if (buyingPublicKey === "DESO") {
    price = price / oneE9;
  } else if (sellingPublicKey === "DESO") {
    price = price * oneE9;
  }

  if (operationType === "ASK") {
    price = oneE76 / price;
  }

  return convertBigIntToDecimalString(price, oneE38);
}

// This calculates ExchangeRateCoinsToSellPerCoinsToBuy as an unscaled coin-level exchange rate for the coin pair.
// The value is always denominated as the number of selling coins per buying coin.
function scaledExchangeRateToUnscaledFloat(
  scaledExchangeRateStr: string,
  buyingPublicKey: string,
  sellingPublicKey: string,
) {
  let scaledExchangeRate = BigInt(scaledExchangeRateStr);
  if (buyingPublicKey === "DESO") {
    scaledExchangeRate = scaledExchangeRate / oneE9;
  } else if (sellingPublicKey === "DESO") {
    scaledExchangeRate = scaledExchangeRate * oneE9;
  }

  return Number(convertBigIntToDecimalString(scaledExchangeRate, oneE38));
}

const getOrderSellingPublicKey = (order: DesoTokenLimitOrder) => {
  return order.creatorSoldAccount?.publicKey ===
    process.env.NEXT_PUBLIC_DESO_ZERO_PUBLIC_KEY ||
    !order.creatorSoldAccount?.publicKey
    ? "DESO"
    : order.creatorSoldAccount.publicKey;
};

const isOrderSellingDESO = (order: DesoTokenLimitOrder) => {
  return (
    order.creatorSoldAccount?.publicKey ===
      process.env.NEXT_PUBLIC_DESO_ZERO_PUBLIC_KEY ||
    !order.creatorSoldAccount?.publicKey
  );
};

const getOrderBuyingPublicKey = (order: DesoTokenLimitOrder) => {
  return order.creatorBoughtAccount?.publicKey ===
    process.env.NEXT_PUBLIC_DESO_ZERO_PUBLIC_KEY ||
    !order.creatorBoughtAccount?.publicKey
    ? "DESO"
    : order.creatorBoughtAccount.publicKey;
};

const isOrderBuyingDESO = (order: DesoTokenLimitOrder) => {
  return (
    order.creatorBoughtAccount?.publicKey ===
      process.env.NEXT_PUBLIC_DESO_ZERO_PUBLIC_KEY ||
    !order.creatorBoughtAccount?.publicKey
  );
};

export const gqlWsOrderToTokenLimitEntry = (
  result: DeSoTokenLimitOrderSubscriptionSubscription | undefined,
): DAOCoinLimitOrderEntryResponse | null => {
  if (!result?.listen?.relatedNode) {
    return null;
  }
  const order = result.listen.relatedNode as DesoTokenLimitOrder;
  const buyingPublicKey = getOrderBuyingPublicKey(order);
  const sellingPublicKey = getOrderSellingPublicKey(order);
  const isBid = order.operationType === BID_OR_ASK_OP.BID_OP;
  const isBuyingDESO = isOrderBuyingDESO(order);
  const isSellingDESO = isOrderSellingDESO(order);
  const isDesoDenominated =
    (isBuyingDESO && isBid) || (isSellingDESO && !isBid);
  const scaledExchangeRate =
    order.scaledExchangeRateCoinsToSellPerCoinToBuyHex ?? "0x0";
  return {
    TransactorPublicKeyBase58Check: order.transactorAccount?.publicKey || "",
    BuyingDAOCoinCreatorPublicKeyBase58Check: buyingPublicKey,
    SellingDAOCoinCreatorPublicKeyBase58Check: sellingPublicKey,
    Quantity: quantityBigIntToDecmialString(
      BigInt(order.quantityToFillInBaseUnitsHex ?? "0x0"),
      isDesoDenominated ? 9 : 18,
      isDesoDenominated ? oneE9 : oneE18,
    ), // TODO: figure out scaling of quantity properly.
    QuantityToFill: isDesoDenominated
      ? nanosToInteger(order.quantityToFillInBaseUnitsHex ?? "0x0")
      : tokenNanosToInteger(order.quantityToFillInBaseUnitsHex ?? "0x0"),
    Price: scaledExchangeRateToPriceString(
      scaledExchangeRate,
      buyingPublicKey,
      sellingPublicKey,
      operationTypeToBidOrAskString(order.operationType),
    ),
    ExchangeRateCoinsToSellPerCoinToBuy: scaledExchangeRateToUnscaledFloat(
      scaledExchangeRate,
      buyingPublicKey,
      sellingPublicKey,
    ),
    OperationType: operationTypeToBidOrAskString(order.operationType),
    OrderID: order.orderId,
  } as DAOCoinLimitOrderEntryResponse;
};
